<template>
  <div class="wrapper">
    <Sidebar :class="!isOpenSidebar ? 'close-sidebar' : ''" />
    <div
      id="wrapper-content"
      :class="[
        !isOpenSidebar ? 'ml-0' : 'content-wrapper-margin',
        'audit-logs-layout',
      ]"
    >
      <PageHeader :isListPage="true" @toggleSidebar="toggleSidebar" />
      <div class="dashboard-wrapper audit-logs-page">
        <!-- Header -->
        <div class="content-top-bar">
          <div class="row align-items-center">
            <div class="col-md-6 flex items-center gap-2">
              <button
                class="action-btn pointer mr-2"
                @click="$router.push('/site-list')"
              >
                <i class="fa-solid fa-arrow-left"></i>
              </button>
              <span class="font-semibold text-base"
                >Audit Logs — {{ siteUri }}</span
              >
            </div>
            <div class="col-md-6">
              <div
                class="content-top-right flex items-center justify-end gap-2"
              >
                <button
                  class="action-btn pointer mr-2"
                  @click="loadLogs"
                  :disabled="loading"
                  title="Refresh"
                >
                  <i
                    class="fa-solid fa-rotate-right"
                    :class="{ 'fa-spin': loading }"
                  ></i>
                </button>
                <button
                  class="btn btn-default btn-bla ck"
                  @click="exportCsv"
                  :disabled="!logs.length"
                >
                  <i class="fa-solid fa-file-csv mr-1"></i> Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="flex flex-wrap gap-3 px-4 py-3 border-b border-gray-200">
          <select
            v-model="filters.action"
            class="px-2 py-1 border border-gray-300 rounded text-sm"
            @change="loadLogs"
          >
            <option value="">All Actions</option>
            <option value="page_view">Page View</option>
            <option value="button_click">Button Click</option>
            <option value="form_submit">Form Submit</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="file_download">File Download</option>
            <option value="link_click">Link Click</option>
            <option value="input_change">Input Change</option>
            <option value="log_access">Log Access</option>
            <option value="log_export">Log Export</option>
          </select>
          <input
            v-model="filters.from"
            type="date"
            class="px-2 py-1 border border-gray-300 rounded text-sm"
            @change="loadLogs"
          />
          <input
            v-model="filters.to"
            type="date"
            class="px-2 py-1 border border-gray-300 rounded text-sm"
            @change="loadLogs"
          />
          <button
            class="text-xs text-gray-400 hover:text-gray-600"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>

        <!-- Table (fixed viewport height; body scrolls inside) -->
        <div class="dashboard-table audit-logs-panel" id="logs-table">
          <div
            v-if="loading"
            class="bg-gray-500 bg-opacity-25 fixed top-0 left-0 w-screen h-screen flex items-center justify-center"
          >
            <div
              class="spinner-border"
              style="width: 3rem; height: 3rem"
              role="status"
            ></div>
          </div>

          <div v-else-if="!logs.length" class="audit-table-empty">
            No audit logs found for this site.
          </div>

          <div v-else class="audit-table-scroll">
            <table class="table audit-logs-table">
              <colgroup>
                <col class="col-datetime" />
                <col class="col-action" />
                <col class="col-email" />
                <col class="col-name" />
                <col class="col-ip" />
                <col class="col-page" />
                <col class="col-element" />
                <col class="col-session" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" class="audit-th">Date &amp; Time</th>
                  <th scope="col" class="audit-th">Action</th>
                  <th scope="col" class="audit-th">User Email</th>
                  <th scope="col" class="audit-th">User Name</th>
                  <th scope="col" class="audit-th">IP Address</th>
                  <th scope="col" class="audit-th">Page</th>
                  <th scope="col" class="audit-th">Element</th>
                  <th scope="col" class="audit-th">Session ID</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(log, i) in logs"
                  :key="log._id"
                  class="audit-row"
                  :class="{ 'audit-row--alt': i % 2 === 1 }"
                >
                  <td class="audit-td audit-td--datetime">
                    {{ formatDate(log.createdAt) }}
                  </td>
                  <td class="audit-td audit-td--action">
                    <span class="action-badge" :class="actionClass(log.action)">
                      {{ formatAction(log.action) }}
                    </span>
                  </td>
                  <td
                    class="audit-td audit-td--truncate"
                    :title="log.user_email || ''"
                  >
                    <span :class="{ 'audit-empty': !log.user_email }">{{
                      log.user_email || "—"
                    }}</span>
                  </td>
                  <td
                    class="audit-td audit-td--truncate"
                    :title="log.user_name || ''"
                  >
                    <span :class="{ 'audit-empty': !log.user_name }">{{
                      log.user_name || "—"
                    }}</span>
                  </td>
                  <td class="audit-td audit-td--mono">
                    {{ log.ip_address || "—" }}
                  </td>
                  <td
                    class="audit-td audit-td--truncate"
                    :title="log.page || ''"
                  >
                    <span :class="{ 'audit-empty': !log.page }">{{
                      log.page || "—"
                    }}</span>
                  </td>
                  <td
                    class="audit-td audit-td--truncate"
                    :title="log.element || log.form_name || ''"
                  >
                    <span
                      :class="{
                        'audit-empty': !(log.element || log.form_name),
                      }"
                      >{{ log.element || log.form_name || "—" }}</span
                    >
                  </td>
                  <td
                    class="audit-td audit-td--mono audit-td--truncate"
                    :title="log.session_id || ''"
                  >
                    {{
                      log.session_id ? log.session_id.slice(0, 10) + "…" : "—"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Server-side pagination (25 per page) -->
          <div v-if="total > 0" class="audit-pagination">
            <span class="audit-pagination-summary">
              Showing {{ rangeStart }}–{{ rangeEnd }} of {{ total }}
              <span class="audit-pagination-muted"
                >({{ pageSize }} per page)</span
              >
            </span>
            <div v-if="totalPages > 1" class="audit-pagination-controls">
              <button
                class="action-btn pointer"
                :disabled="page <= 1 || loading"
                title="First page"
                @click="goToPage(1)"
              >
                <i class="fa-solid fa-angles-left"></i>
              </button>
              <button
                class="action-btn pointer"
                :disabled="page <= 1 || loading"
                title="Previous page"
                @click="goToPage(page - 1)"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <span class="audit-pagination-page"
                >Page {{ page }} of {{ totalPages }}</span
              >
              <button
                class="action-btn pointer"
                :disabled="page >= totalPages || loading"
                title="Next page"
                @click="goToPage(page + 1)"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
              <button
                class="action-btn pointer"
                :disabled="page >= totalPages || loading"
                title="Last page"
                @click="goToPage(totalPages)"
              >
                <i class="fa-solid fa-angles-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "./components/PageHeader";
import Sidebar from "@/components/Sidebar.vue";
import { getAuditLogs, recordAuditExport } from "../service/site";

export default {
  name: "AuditLogs",
  components: { PageHeader, Sidebar },
  data() {
    return {
      loading: false,
      isOpenSidebar: true,
      logs: [],
      total: 0,
      page: 1,
      pageSize: 25,
      totalPages: 0,
      filters: { action: "", from: "", to: "" },
    };
  },
  computed: {
    siteId() {
      return this.$route.params.siteId;
    },
    siteUri() {
      return this.$route.params.uri || this.siteId;
    },
    rangeStart() {
      if (!this.total) return 0;
      return (this.page - 1) * this.pageSize + 1;
    },
    rangeEnd() {
      return Math.min(this.page * this.pageSize, this.total);
    },
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      await this.fetchPage(1);
    },
    async goToPage(p) {
      const next = Math.max(1, Math.min(p, this.totalPages || 1));
      if (next === this.page) return;
      await this.fetchPage(next);
    },
    async fetchPage(page) {
      this.loading = true;
      this.page = page;
      try {
        const res = await getAuditLogs(this.siteId, {
          page: this.page,
          limit: this.pageSize,
          ...this.filters,
        });
        this.logs = res.logs || [];
        this.total = res.total || 0;
        this.page = res.page || page;
        this.pageSize = res.limit || 25;
        this.totalPages =
          res.totalPages ?? Math.ceil(this.total / this.pageSize);
      } catch (err) {
        console.error("Failed to load audit logs", err);
      }
      this.loading = false;
      this.$nextTick(() => {
        const el = this.$el.querySelector(".audit-table-scroll");
        if (el) el.scrollTop = 0;
      });
    },
    clearFilters() {
      this.filters = { action: "", from: "", to: "" };
      this.loadLogs();
    },
    formatDate(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString();
    },
    formatAction(action) {
      const labels = {
        page_view: "Page View",
        button_click: "Button Click",
        form_submit: "Form Submit",
        login: "Login",
        logout: "Logout",
        file_download: "File Download",
        link_click: "Link Click",
        input_change: "Input Change",
        log_access: "Log Access",
        log_export: "Log Export",
      };
      return (
        labels[action] ||
        action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      );
    },
    actionClass(action) {
      const map = {
        login: "badge-green",
        logout: "badge-gray",
        page_view: "badge-blue",
        button_click: "badge-yellow",
        form_submit: "badge-purple",
        file_download: "badge-orange",
        link_click: "badge-yellow",
        input_change: "badge-gray",
        log_access: "badge-blue",
        log_export: "badge-orange",
      };
      return map[action] || "badge-gray";
    },
    async exportCsv() {
      // Record the export as an auditable access event before generating the file
      try {
        await recordAuditExport(this.siteId);
      } catch (err) {
        console.error("Failed to record log export event", err);
      }
      const headers = [
        "Timestamp",
        "Action",
        "User Email",
        "User Name",
        "IP Address",
        "Page",
        "Element / Form",
        "Session ID",
      ];
      const rows = this.logs.map((l) => [
        this.formatDate(l.createdAt),
        l.action,
        l.user_email || "",
        l.user_name || "",
        l.ip_address || "",
        l.page || "",
        l.element || l.form_name || "",
        l.session_id || "",
      ]);
      const csv = [headers, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-logs-${this.siteUri}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    toggleSidebar() {
      this.isOpenSidebar = !this.isOpenSidebar;
    },
  },
};
</script>

<style scoped>
/* Full page fits viewport; only the table body scrolls */
.audit-logs-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.audit-logs-layout > *:not(.audit-logs-page) {
  flex-shrink: 0;
}

.audit-logs-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.audit-logs-page > .content-top-bar,
.audit-logs-page > .flex {
  flex-shrink: 0;
}

.audit-logs-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8e8ee;
  border-radius: 8px;
}

.audit-table-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
}

.audit-table-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.audit-logs-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 0;
}

.audit-logs-table .col-datetime {
  width: 12%;
}
.audit-logs-table .col-action {
  width: 10%;
}
.audit-logs-table .col-email {
  width: 15%;
}
.audit-logs-table .col-name {
  width: 10%;
}
.audit-logs-table .col-ip {
  width: 7%;
}
.audit-logs-table .col-page {
  width: 13%;
}
.audit-logs-table .col-element {
  width: 18%;
}
.audit-logs-table .col-session {
  width: 11%;
}

.audit-logs-table thead th.audit-th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
  text-align: left;
  white-space: nowrap;
  background: #f8f8ff;
  border-bottom: 2px solid #e8e8ee;
}

.audit-logs-table tbody td.audit-td {
  padding: 11px 14px;
  font-size: 13px;
  line-height: 1.35;
  color: #333;
  vertical-align: middle;
  border-bottom: 1px solid #f0f0f4;
}

.audit-row--alt td {
  background: #fafafe;
}

.audit-row:hover td {
  background: #f0faff;
}

.audit-td--datetime {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: #475569;
}

.audit-td--action {
  white-space: nowrap;
}

.audit-td--truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
}

.audit-td--mono {
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #666;
}

.audit-empty {
  color: #d0d0d8;
}

#logs-table.dashboard-table .audit-logs-table th.audit-th,
#logs-table.dashboard-table .audit-logs-table td.audit-td {
  padding: 10px 12px;
}

.action-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-green {
  background: #e8f5ee;
  color: #1a6e40;
}
.badge-gray {
  background: #f0f0f2;
  color: #555;
}
.badge-blue {
  background: #e6f7fd;
  color: #007aaa;
}
.badge-yellow {
  background: #fdf6e3;
  color: #8a6500;
}
.badge-purple {
  background: #f0ecfa;
  color: #5a30b0;
}
.badge-orange {
  background: #fef0e6;
  color: #a04e00;
}
button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.audit-pagination {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 13px;
}

.audit-pagination-summary {
  color: #475569;
  font-weight: 500;
}

.audit-pagination-muted {
  color: #94a3b8;
  font-weight: 400;
}

.audit-pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audit-pagination-page {
  min-width: 7rem;
  text-align: center;
  color: #64748b;
  font-size: 12px;
}
</style>
